import {
  Buf8,
  arraySet,
  assign,
  flattenChunks,
  shrinkBuf,
} from './utils/common.js'
import {
  binstring2buf,
  buf2string,
  string2buf,
  utf8border,
} from './utils/strings.js'
import {
  Z_BUF_ERROR,
  Z_FINISH,
  Z_NEED_DICT,
  Z_NO_FLUSH,
  Z_OK,
  Z_STREAM_END,
  Z_SYNC_FLUSH,
} from './zlib/constants.js'
import GZheader from './zlib/gzheader.js'
import {
  inflate as zlibInflate,
  inflateEnd,
  inflateGetHeader,
  inflateInit2,
  inflateSetDictionary,
} from './zlib/inflate.js'
import msg from './zlib/messages.js'
import ZStream from './zlib/zstream.js'

var toString = Object.prototype.toString

/**
 * class Inflate
 *
 * Generic JS-style wrapper for zlib calls. If you don't need
 * streaming behaviour - use more simple functions: [[inflate]]
 * and [[inflateRaw]].
 **/

/* internal
 * inflate.chunks -> Array
 *
 * Chunks of output data, if [[Inflate#onData]] not overridden.
 **/

/**
 * Inflate.result -> Uint8Array|Array|String
 *
 * Uncompressed result, generated by default [[Inflate#onData]]
 * and [[Inflate#onEnd]] handlers. Filled after you push last chunk
 * (call [[Inflate#push]] with `Z_FINISH` / `true` param) or if you
 * push a chunk with explicit flush (call [[Inflate#push]] with
 * `Z_SYNC_FLUSH` param).
 **/

/**
 * Inflate.err -> Number
 *
 * Error code after inflate finished. 0 (Z_OK) on success.
 * Should be checked if broken data possible.
 **/

/**
 * Inflate.msg -> String
 *
 * Error message, if [[Inflate.err]] != 0
 **/

/**
 * new Inflate(options)
 * - options (Object): zlib inflate options.
 *
 * Creates new inflator instance with specified params. Throws exception
 * on bad params. Supported options:
 *
 * - `windowBits`
 * - `dictionary`
 *
 * [http://zlib.net/manual.html#Advanced](http://zlib.net/manual.html#Advanced)
 * for more information on these.
 *
 * Additional options, for internal needs:
 *
 * - `chunkSize` - size of generated data chunks (16K by default)
 * - `raw` (Boolean) - do raw inflate
 * - `to` (String) - if equal to 'string', then result will be converted
 *   from utf8 to utf16 (javascript) string. When string output requested,
 *   chunk length can differ from `chunkSize`, depending on content.
 *
 * By default, when no options set, autodetect deflate/gzip data format via
 * wrapper header.
 *
 * ##### Example:
 *
 * ```javascript
 * var pako = require('pako')
 *   , chunk1 = Uint8Array([1,2,3,4,5,6,7,8,9])
 *   , chunk2 = Uint8Array([10,11,12,13,14,15,16,17,18,19]);
 *
 * var inflate = new pako.Inflate({ level: 3});
 *
 * inflate.push(chunk1, false);
 * inflate.push(chunk2, true);  // true -> last chunk
 *
 * if (inflate.err) { throw new Error(inflate.err); }
 *
 * console.log(inflate.result);
 * ```
 **/
export class Inflate {
  constructor(options) {
    if (!(this instanceof Inflate)) return new Inflate(options)

    this.options = assign(
      {
        chunkSize: 16384,
        windowBits: 0,
        to: '',
      },
      options || {},
    )

    var opt = this.options

    // Force window size for `raw` data, if not set directly,
    // because we have no header for autodetect.
    if (opt.raw && opt.windowBits >= 0 && opt.windowBits < 16) {
      opt.windowBits = -opt.windowBits
      if (opt.windowBits === 0) {
        opt.windowBits = -15
      }
    }

    // If `windowBits` not defined (and mode not raw) - set autodetect flag for gzip/deflate
    if (
      opt.windowBits >= 0 &&
      opt.windowBits < 16 &&
      !(options && options.windowBits)
    ) {
      opt.windowBits += 32
    }

    // Gzip header has no info about windows size, we can do autodetect only
    // for deflate. So, if window size not set, force it to max when gzip possible
    if (opt.windowBits > 15 && opt.windowBits < 48) {
      // bit 3 (16) -> gzipped data
      // bit 4 (32) -> autodetect gzip/deflate
      if ((opt.windowBits & 15) === 0) {
        opt.windowBits |= 15
      }
    }

    this.err = 0 // error code, if happens (0 = Z_OK)
    this.msg = '' // error message
    this.ended = false // used to avoid multiple onEnd() calls
    this.chunks = [] // chunks of compressed data

    this.strm = new ZStream()
    this.strm.avail_out = 0

    var status = inflateInit2(this.strm, opt.windowBits)

    if (status !== Z_OK) {
      throw new Error(msg[status])
    }

    this.header = new GZheader()

    inflateGetHeader(this.strm, this.header)

    // Setup dictionary
    if (opt.dictionary) {
      // Convert data if needed
      if (typeof opt.dictionary === 'string') {
        opt.dictionary = string2buf(opt.dictionary)
      } else if (toString.call(opt.dictionary) === '[object ArrayBuffer]') {
        opt.dictionary = new Uint8Array(opt.dictionary)
      }
      if (opt.raw) {
        //In raw mode we need to set the dictionary early
        status = inflateSetDictionary(this.strm, opt.dictionary)
        if (status !== Z_OK) {
          throw new Error(msg[status])
        }
      }
    }
  }

  /**
   * Inflate#push(data[, mode]) -> Boolean
   * - data (Uint8Array|Array|ArrayBuffer|String): input data
   * - mode (Number|Boolean): 0..6 for corresponding Z_NO_FLUSH..Z_TREE modes.
   *   See constants. Skipped or `false` means Z_NO_FLUSH, `true` means Z_FINISH.
   *
   * Sends input data to inflate pipe, generating [[Inflate#onData]] calls with
   * new output chunks. Returns `true` on success. The last data block must have
   * mode Z_FINISH (or `true`). That will flush internal pending buffers and call
   * [[Inflate#onEnd]]. For interim explicit flushes (without ending the stream) you
   * can use mode Z_SYNC_FLUSH, keeping the decompression context.
   *
   * On fail call [[Inflate#onEnd]] with error code and return false.
   *
   * We strongly recommend to use `Uint8Array` on input for best speed (output
   * format is detected automatically). Also, don't skip last param and always
   * use the same type in your code (boolean or number). That will improve JS speed.
   *
   * For regular `Array`-s make sure all elements are [0..255].
   *
   * ##### Example
   *
   * ```javascript
   * push(chunk, false); // push one of data chunks
   * ...
   * push(chunk, true);  // push last chunk
   * ```
   **/
  push(data, mode) {
    var strm = this.strm
    var chunkSize = this.options.chunkSize
    var dictionary = this.options.dictionary
    var status, _mode
    var next_out_utf8, tail, utf8str
    var dict

    // Flag to properly process Z_BUF_ERROR on testing inflate call
    // when we check that all output data was flushed.
    var allowBufError = false

    if (this.ended) {
      return false
    }
    _mode = mode === ~~mode ? mode : mode === true ? Z_FINISH : Z_NO_FLUSH

    // Convert data if needed
    if (typeof data === 'string') {
      // Only binary strings can be decompressed on practice
      strm.input = binstring2buf(data)
    } else if (toString.call(data) === '[object ArrayBuffer]') {
      strm.input = new Uint8Array(data)
    } else {
      strm.input = data
    }

    strm.next_in = 0
    strm.avail_in = strm.input.length

    do {
      if (strm.avail_out === 0) {
        strm.output = Buf8(chunkSize)
        strm.next_out = 0
        strm.avail_out = chunkSize
      }

      status = zlibInflate(strm, Z_NO_FLUSH) /* no bad return value */

      if (status === Z_NEED_DICT && dictionary) {
        // Convert data if needed
        if (typeof dictionary === 'string') {
          dict = string2buf(dictionary)
        } else if (toString.call(dictionary) === '[object ArrayBuffer]') {
          dict = new Uint8Array(dictionary)
        } else {
          dict = dictionary
        }

        status = inflateSetDictionary(this.strm, dict)
      }

      if (status === Z_BUF_ERROR && allowBufError === true) {
        status = Z_OK
        allowBufError = false
      }

      if (status !== Z_STREAM_END && status !== Z_OK) {
        this.onEnd(status)
        this.ended = true
        return false
      }

      if (strm.next_out) {
        if (
          strm.avail_out === 0 ||
          status === Z_STREAM_END ||
          (strm.avail_in === 0 &&
            (_mode === Z_FINISH || _mode === Z_SYNC_FLUSH))
        ) {
          if (this.options.to === 'string') {
            next_out_utf8 = utf8border(strm.output, strm.next_out)

            tail = strm.next_out - next_out_utf8
            utf8str = buf2string(strm.output, next_out_utf8)

            // move tail
            strm.next_out = tail
            strm.avail_out = chunkSize - tail
            if (tail) {
              arraySet(strm.output, strm.output, next_out_utf8, tail, 0)
            }

            this.onData(utf8str)
          } else {
            this.onData(shrinkBuf(strm.output, strm.next_out))
          }
        }
      }

      // When no more input data, we should check that internal inflate buffers
      // are flushed. The only way to do it when avail_out = 0 - run one more
      // inflate pass. But if output data not exists, inflate return Z_BUF_ERROR.
      // Here we set flag to process this error properly.
      //
      // NOTE. Deflate does not return error in this case and does not needs such
      // logic.
      if (strm.avail_in === 0 && strm.avail_out === 0) {
        allowBufError = true
      }
    } while (
      (strm.avail_in > 0 || strm.avail_out === 0) &&
      status !== Z_STREAM_END
    )

    if (status === Z_STREAM_END) {
      _mode = Z_FINISH
    }

    // Finalize on the last chunk.
    if (_mode === Z_FINISH) {
      status = inflateEnd(this.strm)
      this.onEnd(status)
      this.ended = true
      return status === Z_OK
    }

    // callback interim results if Z_SYNC_FLUSH.
    if (_mode === Z_SYNC_FLUSH) {
      this.onEnd(Z_OK)
      strm.avail_out = 0
      return true
    }

    return true
  }

  /**
   * Inflate#onData(chunk) -> Void
   * - chunk (Uint8Array|Array|String): output data. Type of array depends
   *   on js engine support. When string output requested, each chunk
   *   will be string.
   *
   * By default, stores data blocks in `chunks[]` property and glue
   * those in `onEnd`. Override this handler, if you need another behaviour.
   **/
  onData(chunk) {
    this.chunks.push(chunk)
  }

  /**
   * Inflate#onEnd(status) -> Void
   * - status (Number): inflate status. 0 (Z_OK) on success,
   *   other if not.
   *
   * Called either after you tell inflate that the input stream is
   * complete (Z_FINISH) or should be flushed (Z_SYNC_FLUSH)
   * or if an error happened. By default - join collected chunks,
   * free memory and fill `results` / `err` properties.
   **/
  onEnd(status) {
    // On success - join
    if (status === Z_OK) {
      if (this.options.to === 'string') {
        // Glue & convert here, until we teach pako to send
        // utf8 aligned strings to onData
        this.result = this.chunks.join('')
      } else {
        this.result = flattenChunks(this.chunks)
      }
    }
    this.chunks = []
    this.err = status
    this.msg = this.strm.msg
  }
}

/**
 * inflate(data[, options]) -> Uint8Array|Array|String
 * - data (Uint8Array|Array|String): input data to decompress.
 * - options (Object): zlib inflate options.
 *
 * Decompress `data` with inflate/ungzip and `options`. Autodetect
 * format via wrapper header by default. That's why we don't provide
 * separate `ungzip` method.
 *
 * Supported options are:
 *
 * - windowBits
 *
 * [http://zlib.net/manual.html#Advanced](http://zlib.net/manual.html#Advanced)
 * for more information.
 *
 * Sugar (options):
 *
 * - `raw` (Boolean) - say that we work with raw stream, if you don't wish to specify
 *   negative windowBits implicitly.
 * - `to` (String) - if equal to 'string', then result will be converted
 *   from utf8 to utf16 (javascript) string. When string output requested,
 *   chunk length can differ from `chunkSize`, depending on content.
 *
 *
 * ##### Example:
 *
 * ```javascript
 * var pako = require('pako')
 *   , input = pako.deflate([1,2,3,4,5,6,7,8,9])
 *   , output;
 *
 * try {
 *   output = pako.inflate(input);
 * } catch (err)
 *   console.log(err);
 * }
 * ```
 **/
export function inflate(input, options) {
  var inflator = new Inflate(options)

  inflator.push(input, true)

  // That will never happens, if you don't cheat with options :)
  if (inflator.err) {
    throw inflator.msg || msg[inflator.err]
  }

  return inflator.result
}

/**
 * inflateRaw(data[, options]) -> Uint8Array|Array|String
 * - data (Uint8Array|Array|String): input data to decompress.
 * - options (Object): zlib inflate options.
 *
 * The same as [[inflate]], but creates raw data, without wrapper
 * (header and adler32 crc).
 **/
export function inflateRaw(input, options) {
  options = options || {}
  options.raw = true
  return inflate(input, options)
}

/**
 * ungzip(data[, options]) -> Uint8Array|Array|String
 * - data (Uint8Array|Array|String): input data to decompress.
 * - options (Object): zlib inflate options.
 *
 * Just shortcut to [[inflate]], because it autodetects format
 * by header.content. Done for convenience.
 **/
export var ungzip = inflate
